import { ABILITIES } from "../content/abilities";
import { CLASSES, HERO_ORDER } from "../content/classes";
import { STATUSES } from "../content/statuses";
import {
  ABILITY_NUM_KEYS,
  applyBaselineHpToHeroes,
  bindLabel,
  binds,
  CLASS_NUM_KEYS,
  COMBAT_FIELDS,
  combat,
  persistTuning,
  resetTuning,
  STATUS_NUM_KEYS,
} from "../content/tuning";

let root: HTMLElement | null = null;
let open = false;
let filter = "";
let capturing: keyof typeof binds | null = null;

export function tunerOpen(): boolean {
  return open;
}

export function setTunerOpen(next: boolean): void {
  open = next;
  if (root) root.style.display = open ? "flex" : "none";
  if (open) renderTuner();
}

export function toggleTuner(): void {
  setTunerOpen(!open);
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function numInput(value: number, step: number, onChange: (v: number) => void): HTMLInputElement {
  const input = el("input", "tuner-num");
  input.type = "number";
  input.step = String(step);
  input.value = String(value);
  input.addEventListener("change", () => {
    const v = Number(input.value);
    if (!Number.isFinite(v)) {
      input.value = String(value);
      return;
    }
    onChange(v);
    persistTuning();
  });
  return input;
}

function matches(text: string): boolean {
  if (!filter) return true;
  return text.toLowerCase().includes(filter.toLowerCase());
}

function section(title: string): HTMLElement {
  const wrap = el("section", "tuner-sec");
  wrap.append(el("h3", "", title));
  return wrap;
}

function row(label: string, control: HTMLElement, hint?: string): HTMLElement {
  const r = el("div", "tuner-row");
  const lab = el("label", "", label);
  r.append(lab, control);
  if (hint) {
    const h = el("div", "tuner-hint", hint);
    r.append(h);
  }
  return r;
}

export function mountTuner(): void {
  if (root) return;
  root = el("aside", "tuner");
  root.style.display = "none";
  document.body.append(root);
  window.addEventListener("keydown", (e) => {
    if (!capturing) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    e.preventDefault();
    e.stopPropagation();
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
    binds[capturing] = key === "spacebar" ? " " : key;
    capturing = null;
    persistTuning();
    renderTuner();
  }, true);
}

function renderTuner(): void {
  if (!root) return;
  root.innerHTML = "";

  const head = el("header", "tuner-head");
  head.append(el("strong", "", "Tuner"));
  const search = el("input", "tuner-search");
  search.type = "search";
  search.placeholder = "filter…";
  search.value = filter;
  search.addEventListener("input", () => {
    filter = search.value;
    renderTuner();
    const again = root?.querySelector<HTMLInputElement>(".tuner-search");
    if (again) {
      again.focus();
      again.setSelectionRange(filter.length, filter.length);
    }
  });
  const reset = el("button", "tuner-btn", "Reset");
  reset.addEventListener("click", () => {
    resetTuning();
    renderTuner();
  });
  const close = el("button", "tuner-btn", "Close");
  close.addEventListener("click", () => setTunerOpen(false));
  head.append(search, reset, close);
  root.append(head);

  const body = el("div", "tuner-body");

  const combatSec = section("Combat");
  for (const field of COMBAT_FIELDS) {
    if (!matches(field.label + field.key)) continue;
    combatSec.append(
      row(
        field.label,
        numInput(combat[field.key], field.step, (v) => {
          combat[field.key] = v;
        }),
        field.hint,
      ),
    );
  }
  if (matches("baseline")) {
    const apply = el("button", "tuner-btn", "Apply baseline HP to heroes");
    apply.addEventListener("click", () => {
      applyBaselineHpToHeroes();
      renderTuner();
    });
    combatSec.append(apply);
  }
  if (combatSec.childElementCount > 1) body.append(combatSec);

  const bindSec = section("Binds");
  (Object.keys(binds) as (keyof typeof binds)[]).forEach((id) => {
    if (!matches(id + binds[id])) return;
    const btn = el("button", "tuner-bind", capturing === id ? "press a key…" : bindLabel(binds[id]));
    btn.addEventListener("click", () => {
      capturing = id;
      renderTuner();
    });
    bindSec.append(row(id, btn));
  });
  if (bindSec.childElementCount > 1) body.append(bindSec);

  const classSec = section("Classes");
  for (const id of [...HERO_ORDER, "skeleton", "golem", "spirit"] as const) {
    const def = CLASSES[id];
    if (!matches(def.name + id)) continue;
    const block = el("div", "tuner-block");
    block.append(el("h4", "", def.name));
    for (const key of CLASS_NUM_KEYS) {
      block.append(
        row(
          key,
          numInput(def[key], key === "maxHp" ? 1 : 0.05, (v) => {
            def[key] = v;
          }),
        ),
      );
    }
    classSec.append(block);
  }
  if (classSec.childElementCount > 1) body.append(classSec);

  const abilSec = section("Abilities");
  for (const def of Object.values(ABILITIES)) {
    if (!matches(def.name + def.id + def.classId)) continue;
    const block = el("div", "tuner-block");
    block.append(el("h4", "", `${def.name}  (${def.classId} ${def.slot})`));
    for (const key of ABILITY_NUM_KEYS) {
      const cur = def[key];
      if (typeof cur !== "number") continue;
      block.append(
        row(
          key,
          numInput(cur, key === "cd" || key === "damage" || key === "heal" ? 0.5 : 0.05, (v) => {
            (def as unknown as Record<string, number>)[key] = v;
          }),
        ),
      );
    }
    abilSec.append(block);
  }
  if (abilSec.childElementCount > 1) body.append(abilSec);

  const stSec = section("Statuses");
  for (const def of Object.values(STATUSES)) {
    if (!matches(def.name + def.id)) continue;
    const block = el("div", "tuner-block");
    block.append(el("h4", "", def.name));
    for (const key of STATUS_NUM_KEYS) {
      block.append(
        row(
          key,
          numInput(def[key], 0.1, (v) => {
            def[key] = v;
          }),
        ),
      );
    }
    stSec.append(block);
  }
  if (stSec.childElementCount > 1) body.append(stSec);

  root.append(body);
}
